/**
 * Memory Monitor Utility
 * Утилита для мониторинга использования памяти и обнаружения утечек
 */

class MemoryMonitor {
  constructor() {
    this.snapshots = []
    this.isMonitoring = false
    this.monitoringInterval = null
    this.tabSwitchCount = 0
    this.routeTabMemoryBaseline = null
  }

  /**
   * Получить текущую информацию о памяти
   */
  getMemoryInfo() {
    if (!performance.memory) {
      console.warn('Performance.memory API не поддерживается в этом браузере')
      return null
    }

    const memory = performance.memory
    return {
      used: Math.round(memory.usedJSHeapSize / 1024 / 1024), // MB
      total: Math.round(memory.totalJSHeapSize / 1024 / 1024), // MB
      limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024), // MB
      timestamp: Date.now(),
      tabSwitchCount: this.tabSwitchCount,
    }
  }

  /**
   * Создать снимок памяти с меткой
   */
  takeSnapshot(label = 'Unknown') {
    const memoryInfo = this.getMemoryInfo()
    if (!memoryInfo) return null

    const snapshot = {
      label,
      ...memoryInfo,
      datetime: new Date().toLocaleString(),
    }

    this.snapshots.push(snapshot)

    // Ограничиваем количество снимков
    if (this.snapshots.length > 50) {
      this.snapshots.shift()
    }

    console.log(`📊 Memory Snapshot [${label}]: ${memoryInfo.used} MB used, ${memoryInfo.total} MB total`)
    return snapshot
  }

  /**
   * Установить базовую линию памяти для маршрутных табов
   */
  setRouteTabBaseline() {
    this.routeTabMemoryBaseline = this.getMemoryInfo()
    console.log(`📈 Route Tab Memory Baseline: ${this.routeTabMemoryBaseline.used} MB`)
  }

  /**
   * Проверить утечку памяти при переключении табов
   */
  checkRouteTabMemoryLeak() {
    if (!this.routeTabMemoryBaseline) {
      console.warn('⚠️ Route tab memory baseline не установлен')
      return null
    }

    const currentMemory = this.getMemoryInfo()
    if (!currentMemory) return null

    const memoryDiff = currentMemory.used - this.routeTabMemoryBaseline.used
    const leakThreshold = 50 // MB

    const result = {
      baseline: this.routeTabMemoryBaseline.used,
      current: currentMemory.used,
      diff: memoryDiff,
      tabSwitches: this.tabSwitchCount,
      isLeak: memoryDiff > leakThreshold,
      severity: this.getLeakSeverity(memoryDiff),
    }

    if (result.isLeak) {
      console.error(`🚨 MEMORY LEAK DETECTED! Увеличение на ${memoryDiff} MB за ${this.tabSwitchCount} переключений табов`)
    } else {
      console.log(`✅ Memory check OK: ${memoryDiff >= 0 ? '+' : ''}${memoryDiff} MB`)
    }

    return result
  }

  /**
   * Определить серьезность утечки памяти
   */
  getLeakSeverity(memoryDiff) {
    if (memoryDiff < 20) return 'low'
    if (memoryDiff < 50) return 'medium'
    if (memoryDiff < 100) return 'high'
    return 'critical'
  }

  /**
   * Зарегистрировать переключение таба
   */
  registerTabSwitch(fromTab, toTab) {
    this.tabSwitchCount++
    this.takeSnapshot(`Tab Switch ${this.tabSwitchCount}: ${fromTab} → ${toTab}`)

    // Проверяем утечку памяти каждые 5 переключений
    if (this.tabSwitchCount % 5 === 0) {
      this.checkRouteTabMemoryLeak()
    }
  }

  /**
   * Сравнить два снимка памяти
   */
  compareSnapshots(snapshot1, snapshot2) {
    if (!snapshot1 || !snapshot2) return null

    return {
      usedDiff: snapshot2.used - snapshot1.used,
      totalDiff: snapshot2.total - snapshot1.total,
      timeDiff: snapshot2.timestamp - snapshot1.timestamp,
      label1: snapshot1.label,
      label2: snapshot2.label,
    }
  }

  /**
   * Получить последние N снимков
   */
  getRecentSnapshots(count = 10) {
    return this.snapshots.slice(-count)
  }

  /**
   * Принудительно запустить сборку мусора (если доступно)
   */
  forceGarbageCollection() {
    if (window.gc) {
      console.log('🗑️ Принудительная сборка мусора...')
      window.gc()
      setTimeout(() => {
        this.takeSnapshot('After GC')
      }, 100)
    } else {
      console.warn('⚠️ Сборка мусора недоступна. Запустите Chrome с флагом --js-flags="--expose-gc"')
    }
  }

  /**
   * Начать автоматический мониторинг
   */
  startAutoMonitoring(intervalMs = 30000) {
    if (this.isMonitoring) {
      console.warn('⚠️ Мониторинг уже запущен')
      return
    }

    this.isMonitoring = true
    this.takeSnapshot('Auto Monitoring Started')

    this.monitoringInterval = setInterval(() => {
      const snapshot = this.takeSnapshot('Auto Monitor')

      // Проверяем критический рост памяти
      if (snapshot && snapshot.used > 800) {
        // > 800MB
        console.error('🚨 CRITICAL MEMORY USAGE:', snapshot.used, 'MB')
        this.forceGarbageCollection()
      }
    }, intervalMs)

    console.log('🔄 Автоматический мониторинг памяти запущен')
  }

  /**
   * Остановить автоматический мониторинг
   */
  stopAutoMonitoring() {
    if (!this.isMonitoring) return

    this.isMonitoring = false
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval)
      this.monitoringInterval = null
    }

    this.takeSnapshot('Auto Monitoring Stopped')
    console.log('⏹️ Автоматический мониторинг памяти остановлен')
  }

  /**
   * Получить отчет о памяти
   */
  getMemoryReport() {
    const recent = this.getRecentSnapshots(5)
    const current = this.getMemoryInfo()

    return {
      current,
      recentSnapshots: recent,
      totalSnapshots: this.snapshots.length,
      tabSwitchCount: this.tabSwitchCount,
      baseline: this.routeTabMemoryBaseline,
      leakCheck: this.checkRouteTabMemoryLeak(),
    }
  }

  /**
   * Логировать подробный отчет в консоль
   */
  logDetailedReport() {
    const report = this.getMemoryReport()

    console.group('📊 Memory Monitor Report')
    console.log('Current Memory:', report.current)
    console.log('Tab Switches:', report.tabSwitchCount)
    console.log('Baseline:', report.baseline)
    console.log('Leak Check:', report.leakCheck)
    console.log('Recent Snapshots:', report.recentSnapshots)
    console.groupEnd()
  }

  /**
   * Очистить все снимки
   */
  clearSnapshots() {
    this.snapshots = []
    this.tabSwitchCount = 0
    this.routeTabMemoryBaseline = null
    console.log('🧹 Снимки памяти очищены')
  }

  /**
   * Сравнить два снимка по меткам
   * @param {string} label1 - метка первого снимка
   * @param {string} label2 - метка второго снимка
   */
  diff(label1, label2) {
    const snapshot1 = this.snapshots.find(s => s.label === label1)
    const snapshot2 = this.snapshots.find(s => s.label === label2)

    if (!snapshot1) {
      console.warn(`⚠️ Снимок с меткой "${label1}" не найден`)
      return null
    }

    if (!snapshot2) {
      console.warn(`⚠️ Снимок с меткой "${label2}" не найден`)
      return null
    }

    const comparison = this.compareSnapshots(snapshot1, snapshot2)

    console.group(`🔍 Memory Diff: ${label1} → ${label2}`)
    console.log(`Used Memory: ${comparison.usedDiff >= 0 ? '+' : ''}${comparison.usedDiff} MB`)
    console.log(`Total Memory: ${comparison.totalDiff >= 0 ? '+' : ''}${comparison.totalDiff} MB`)
    console.log(`Time Elapsed: ${Math.round(comparison.timeDiff / 1000)}s`)

    if (Math.abs(comparison.usedDiff) > 10) {
      console.warn(`⚠️ Значительное изменение памяти: ${comparison.usedDiff} MB`)
    }

    console.groupEnd()

    return comparison
  }
}

// Создаем глобальный экземпляр
const memoryMonitor = new MemoryMonitor()

// Экспортируем для использования в приложении
export default memoryMonitor

// Добавляем в глобальную область для отладки
if (typeof window !== 'undefined') {
  window.memoryMonitor = memoryMonitor
}
